import React from 'react';
import { Edit } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';

const PricingTab = ({
  pricing,
  pricingSettings,
  savingPricingSettings,
  togglePricingEnforcement,
  handleTogglePricingEnforcement,
  handleEditPricing,
}) => {
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle>Subscription Pricing</CardTitle>
        <CardDescription>Configure pricing for each subscription tier</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="font-medium text-white">Enforce Pricing Structure</div>
              <p className="mt-1 text-sm text-zinc-500">
                When enabled, plan upgrades require Stripe checkout. When disabled, paid plans can be activated without payment for family testing.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant={pricingSettings.payments_enforced ? 'default' : 'secondary'}
                size="sm"
                onClick={togglePricingEnforcement}
                disabled={savingPricingSettings}
                className="min-w-[180px] justify-center"
              >
                {pricingSettings.payments_enforced ? 'Payments Enforced' : 'Family Test Mode'}
              </Button>
              <Switch
                checked={Boolean(pricingSettings.payments_enforced)}
                disabled={savingPricingSettings}
                onCheckedChange={handleTogglePricingEnforcement}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(pricing).map(([plan, config]) => (
            <Card key={plan} className="bg-zinc-800 border-zinc-700">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{plan}</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditPricing(plan)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Monthly</span>
                  <span className="font-semibold">${config.price_monthly}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Yearly</span>
                  <span className="font-semibold">${config.price_yearly}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Accounts</span>
                  <span>{config.max_accounts === -1 ? 'Unlimited' : config.max_accounts}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Strategies</span>
                  <span>{config.max_strategies === -1 ? 'Unlimited' : config.max_strategies}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Custom Scripts</span>
                  <span>{config.custom_strategy_limit === -1 ? 'Unlimited' : config.custom_strategy_limit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Visual Strategies</span>
                  <span>{config.visual_strategy_limit === -1 ? 'Unlimited' : config.visual_strategy_limit}</span>
                </div>
                <div className="pt-1 text-xs text-zinc-500">
                  {(config.allowed_instruments || []).join(', ') || 'No instruments set'}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PricingTab;
